/**
 * CampaignRecurrenceCard — drop-in recurrence editor for an existing campaign.
 * Usage: <CampaignRecurrenceCard campaignId={id} />
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Repeat, History, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export default function CampaignRecurrenceCard({ campaignId, existing }) {
  const { token } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}`, 'Content-Type': 'application/json' });
  const qc = useQueryClient();

  const [rule, setRule] = useState(existing?.recurrence?.rule || 'one_time');
  const [intervalDays, setIntervalDays] = useState(existing?.recurrence?.interval_days || 30);
  const [cycleDay, setCycleDay] = useState(existing?.recurrence?.cycle_day || 1);
  const [resetOnCycle, setResetOnCycle] = useState(existing?.recurrence?.reset_on_cycle ?? true);

  useEffect(() => {
    if (existing?.recurrence) {
      setRule(existing.recurrence.rule || 'one_time');
      setIntervalDays(existing.recurrence.interval_days || 30);
      setCycleDay(existing.recurrence.cycle_day || 1);
      setResetOnCycle(existing.recurrence.reset_on_cycle ?? true);
    }
  }, [existing?.recurrence]);

  const { data: cycles = { items: [] } } = useQuery({
    queryKey: ['campaign-cycles', campaignId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/recurring/campaigns/${campaignId}/cycles`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/recurring/campaigns/${campaignId}/recurrence`, {
        method: 'POST',
        headers: hdrs(),
        body: JSON.stringify({ rule, interval_days: Number(intervalDays), cycle_day: Number(cycleDay), reset_on_cycle: resetOnCycle }),
      });
      if (!r.ok) throw new Error('Save failed');
      return r.json();
    },
    onSuccess: (d) => {
      toast.success('Recurrence saved');
      qc.invalidateQueries({ queryKey: ['campaign-cycles', campaignId] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="campaign-recurrence-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Repeat className="h-4 w-4 text-indigo-500" /> Recurrence
          {existing?.recurrence?.cycle_number > 0 && (
            <Badge variant="outline" className="ml-2">Cycle {existing.recurrence.cycle_number}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Rule</Label>
            <Select value={rule} onValueChange={setRule}>
              <SelectTrigger data-testid="campaign-rule-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="one_time">One-time (no recurrence)</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Bi-weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="custom">Custom days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {rule === 'custom' && (
            <div>
              <Label className="text-xs">Interval (days)</Label>
              <Input type="number" min="1" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} />
            </div>
          )}
          {rule === 'monthly' && (
            <div>
              <Label className="text-xs">Cycle day of month</Label>
              <Input type="number" min="1" max="31" value={cycleDay} onChange={(e) => setCycleDay(e.target.value)} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={resetOnCycle} onCheckedChange={setResetOnCycle} id="reset-cycle" data-testid="campaign-reset-cycle-switch" />
          <Label htmlFor="reset-cycle" className="text-sm cursor-pointer">Reset raised amount at each cycle rollover</Label>
        </div>
        {existing?.recurrence?.next_run_at && (
          <p className="text-xs text-slate-500">Next cycle rolls at {new Date(existing.recurrence.next_run_at).toLocaleString()}.</p>
        )}
        <Button disabled={save.isPending} onClick={() => save.mutate()} data-testid="campaign-save-recurrence">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save recurrence
        </Button>

        {cycles.items?.length > 0 && (
          <div className="pt-4 border-t">
            <p className="text-sm font-semibold flex items-center gap-2 mb-2"><History className="h-4 w-4 text-slate-500" /> Cycle history</p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Ended</TableHead>
                  <TableHead className="text-right">Raised</TableHead>
                  <TableHead className="text-right">Donors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cycles.items.map((c) => (
                  <TableRow key={c._id} data-testid={`cycle-row-${c.cycle_number}`}>
                    <TableCell className="font-mono text-sm">{c.cycle_number}</TableCell>
                    <TableCell className="text-xs">{c.started_at ? new Date(c.started_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-xs">{c.ended_at ? new Date(c.ended_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-right font-medium">${Number(c.raised_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">{c.donor_count || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
